import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Stack, TextField, Button, Typography, Alert, Card, CardContent } from '@metarouter-private/mr-mui';
import { useCreateWebhookConfig } from '@ion/api';
import isValidWebhookUrl from 'app/settings/webhook-settings-page/isValidWebhookUrl';
import EventTypeCheckboxes from './EventTypeCheckboxes';

export default function CreateWebhookForm({ onCancel, onCreated }) {
  const [url, setUrl] = useState('');
  const [urlTouched, setUrlTouched] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [error, setError] = useState(null);

  const isUrlValid = isValidWebhookUrl(url);
  const urlError = urlTouched && Boolean(url.trim()) && !isUrlValid;
  const isFormValid = isUrlValid && eventTypes.length > 0;

  const [createWebhook, { loading }] = useCreateWebhookConfig({
    onCompleted: result => onCreated(result.hmac_secret),
    onError: err => setError(err.message),
  });

  const handleSubmit = event => {
    event.preventDefault();
    setError(null);
    if (!isFormValid) {
      setError('Please enter a valid URL (e.g. https://example.com/webhook)');
      return;
    }
    createWebhook({ url: url.trim(), eventTypes, enabled: true });
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack component="form" onSubmit={handleSubmit} spacing={3}>
          <Typography variant="h6">Create Webhook Configuration</Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Webhook URL"
            value={url}
            onChange={event => setUrl(event.target.value)}
            onBlur={() => setUrlTouched(true)}
            placeholder="https://example.com/webhook"
            helperText={
              urlError
                ? 'Please enter a valid URL (e.g. https://example.com/webhook)'
                : 'The URL that will receive webhook event payloads.'
            }
            error={urlError}
            required
            fullWidth
          />
          <Stack spacing={1}>
            <Typography variant="subtitle2">Event Types</Typography>
            <Typography variant="body2" color="text.secondary">
              Select which events should trigger this webhook.
            </Typography>
            <EventTypeCheckboxes selectedTypes={eventTypes} onChange={setEventTypes} />
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="text" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!isFormValid || loading}>
              {loading ? 'Creating...' : 'Create Webhook Configuration'}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

CreateWebhookForm.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};
